import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Input from 'components/Input';
import SelectList from './SelectList';

const SearchableSelectBox = (props) => {
  const {
    className = '',
    label = '',
    readOnly = false,
    labelElement = null,
    id = '',
    inputValue = null,
    onChange = () => {},
    placeholder = '',
    type = 'text',
    inputErrors = '',
    inputWrapperClassName = '',
    inputClassName = '',
    maxLength = 60,
    isInputDisabled = false,
    inputName = '',
    inputInlineElement = null,
    items = [],
    selectListClassName = '',
    itemClassName = '',
    selectedItem = null,
    onSelect = () => {},
    isLoading = false,
    required = false,
    showDefault = false,
    autoComplete = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const selectWrapperRef = useRef(null);
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        !isOpen && setIsOpen(!isOpen);
        active !== items.length ? setActive(active + 1) : setActive(0);
        break;

      case 'ArrowUp':
        e.preventDefault();
        active !== 0 && setActive(active - 1);
        if (active === 0) {
          setIsOpen(false);
          setActive(-1);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setActive(-1);
        break;
      case 'Enter':
        e.preventDefault();
        onSelect(items[active]);
        setActive(-1);
        isOpen && setIsOpen(false);
    }
  };

  useEffect(() => {
    selectWrapperRef?.current?.addEventListener('keydown', handleKeyDown);
    return () => {
      selectWrapperRef?.current?.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, active, items]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (!selectWrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setIsOpen(!!e.target.value.length);
  };

  const handleOnClick = (e) => {
    if (e.target.value.length || showDefault) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div ref={selectWrapperRef} className={`w-full relative ${className}`}>
      <Input
        label={label}
        labelElement={labelElement}
        placeholder={placeholder}
        type={type}
        readOnly={readOnly}
        errors={inputErrors}
        inputWrapperClassName={inputWrapperClassName}
        inputClassName={inputClassName}
        maxLength={maxLength}
        required={required}
        disabled={isInputDisabled}
        name={inputName}
        inlineElement={inputInlineElement}
        id={id}
        value={inputValue}
        onClick={handleOnClick}
        autoComplete={autoComplete}
        onChange={handleInputChange}
      />
      {!readOnly && (
        <SelectList
          className={`${isOpen ? 'block' : 'hidden'} ${selectListClassName}`}
          itemClassName={itemClassName}
          active={active}
          items={items}
          selectedItem={selectedItem}
          onClick={(item) => {
            setIsOpen(!isOpen);
            onSelect(item);
          }}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

SearchableSelectBox.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  id: PropTypes.string,
  inputValue: PropTypes.any.isRequired,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  items: PropTypes.array,
  selectListClassName: PropTypes.string,
  itemClassName: PropTypes.string,
  selectedItem: PropTypes.object,
  isLoading: PropTypes.bool,
  labelElement: PropTypes.any,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  inputErrors: PropTypes.string,
  inputHeaderWrapperClassName: PropTypes.string,
  inputWrapperClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  maxLength: PropTypes.number,
  isInputDisabled: PropTypes.bool,
  required: PropTypes.bool,
  inputName: PropTypes.string,
  inputInlineElement: PropTypes.any,
  showDefault: PropTypes.bool,
  readOnly: PropTypes.bool,
  autoComplete: PropTypes.bool,
};

export default SearchableSelectBox;
